import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Input from "../../components/Input";
import { useSnackbar } from "../../context/SnackbarContext";
import { stockService } from "../../services/stock/stockService";
import { userService } from "../../services/user/userService";
import NumbersIcon from "@mui/icons-material/Numbers";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PercentIcon from "@mui/icons-material/Percent";
import {
  Society,
  Stock,
  StockStatusResponse,
  Product,
} from "../../types/api.types";
import { formatDate } from "../../utils/date";
import DataTable from "../../components/DataTable";
import { productService } from "../../services/product/productService";
import { societyService } from "../../services/society/societyService";

const CODE_WIDTH = 150;

interface StockFormData {
  estimatedArribalDate: string;
  arribalDate: string;
  observation: string;
  companyId: string;
  code: string;
  percentageSafety: string;
}

interface ArticleFormData {
  nombre: string;
  productId: string;
  code: string;
  cantidad: string;
}

interface StockDetail {
  stockHeaderId: string;
  code: string;
  observation: string;
  estimatedArribalDate: string;
  arribalDate?: string;
  percentageSafety: string;
  company: string;
  status: string;
  createDate: string;
  companyId: string;
}

interface ArticleItem {
  stockItemId: string;
  productId: string;
  productCode: string;
  productDescription: string;
  quantity: number;
  rest: number;
}

/** Resto estimado: cantidad menos el fallo aplicado como porcentaje sobre la cantidad. */
function computeArticleRest(quantity: number, failurePercent: number): number {
  if (!Number.isFinite(quantity) || quantity < 0) return 0;
  const pct = Number.isFinite(failurePercent) ? failurePercent : 0;
  return Math.round(quantity - (quantity * pct) / 100);
}

const StockEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [societies, setSocieties] = useState<Society[]>([]);
  const [statuses, setStatuses] = useState<StockStatusResponse[]>([]);
  const [stockData, setStockData] = useState<StockDetail | null>(null);
  const [societyOpen, setSocietyOpen] = useState<boolean>(false);

  const { showSnackbar } = useSnackbar();
  const [productSearch, setProductSearch] = useState<string>("");
  const [articlePage, setArticlePage] = useState<number>(0);
  const [articleRowsPerPage] = useState<number>(10);
  const [articles, setArticles] = useState<any[]>([]);
  const [articlesTotal, setArticlesTotal] = useState<number>(0);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingArticle, setEditingArticle] = useState<any | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productInputValue, setProductInputValue] = useState<string>("");
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [productOpen, setProductOpen] = useState<boolean>(false);
  const [loadingSocieties, setLoadingSocieties] = useState<boolean>(false);
  const [societyInputValue, setSocietyInputValue] = useState<string>("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<StockFormData>({
    defaultValues: {
      estimatedArribalDate: "",
      arribalDate: "",
      observation: "",
      companyId: "",
      code: "",
      percentageSafety: "",
    },
  });

  const {
    control: articleControl,
    handleSubmit: handleArticleSubmit,
    formState: { errors: articleErrors },
    reset: resetArticleForm,
    setValue: setArticleValue,
    watch: watchArticle,
  } = useForm<ArticleFormData>({
    defaultValues: {
      nombre: "",
      code: "",
      productId: "",
      cantidad: "",
    },
  });

  const cantidadWatched = watchArticle("cantidad");
  const failurePct = stockData?.percentageSafety ?? "0";
  const restoPreview =
    cantidadWatched !== undefined &&
    cantidadWatched !== "" &&
    /^[0-9]+$/.test(String(cantidadWatched).trim())
      ? computeArticleRest(Number(cantidadWatched), Number(failurePct))
      : null;

  // Búsqueda asíncrona de sociedades cada vez que el usuario escribe
  useEffect(() => {
    let active = true;

    if (!societyOpen) {
      return undefined;
    }

    const fetchSocieties = async () => {
      setLoadingSocieties(true);
      try {
        const societiesResponse = await societyService.getSocieties(
          0,
          30,
          societyInputValue,
        );

        if (active) {
          setSocieties((societiesResponse?.companies as any) || []);
        }
      } catch (error: any) {
        if (active) {
          showSnackbar(
            error.response?.data?.message || "Error al buscar sociedades",
            "error",
            4000,
          );
          setSocieties([]);
        }
      } finally {
        if (active) {
          setLoadingSocieties(false);
        }
      }
    };

    // Debounce para evitar demasiadas peticiones
    const delayDebounceFn = setTimeout(() => {
      fetchSocieties();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [societyInputValue, societyOpen, showSnackbar]);

  // Búsqueda asíncrona de productos
  useEffect(() => {
    let active = true;

    if (!productOpen) {
      return undefined;
    }

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const productsResponse = await productService.getProducts(
          0,
          30,
          productInputValue,
        );

        if (active) {
          setProducts(productsResponse?.products || []);
        }
      } catch (error: any) {
        if (active) {
          showSnackbar(
            error.response?.data?.message || "Error al buscar productos",
            "error",
            4000,
          );
          setProducts([]);
        }
      } finally {
        if (active) {
          setLoadingProducts(false);
        }
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [productInputValue, productOpen, showSnackbar]);

  const articleColumns = [
    {
      header: "Código Producto",
      key: "code",
    },
    {
      header: "Nombre",
      key: "description",
    },
    {
      header: "Cantidad",
      key: "quantity",
    },
    {
      header: "Resto",
      key: "rest",
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: ArticleItem) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditArticle(row)}
            sx={{ mr: 1 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {/* <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteArticle(row.stockItemId)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton> */}
        </>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const societiesResponse = await userService.getCompanies();
        setSocieties(societiesResponse as any[]);

        const statusesResponse = await stockService.getStatuses();
        setStatuses(statusesResponse);
      } catch (error: any) {
        showSnackbar(
          error.response?.data?.message || "Error al cargar datos",
          "error",
          4000,
        );
      }
    };
    fetchData();
  }, []);

  // Cargar items del stock con paginación y búsqueda
  useEffect(() => {
    const fetchItems = async () => {
      if (!id) return;

      try {
        setLoadingArticles(true);
        const response = await stockService.getItems(
          id,
          articlePage,
          articleRowsPerPage,
          productSearch,
        );
        setArticles(response?.stockItemResponses || response?.items || []);
        setArticlesTotal(response?.total || 0);
      } catch (error: any) {
        showSnackbar(
          error.response?.data?.message || "Error al cargar los items",
          "error",
          4000,
        );
        setArticles([]);
        setArticlesTotal(0);
      } finally {
        setLoadingArticles(false);
      }
    };

    // Debounce para la búsqueda
    const delayDebounceFn = setTimeout(() => {
      fetchItems();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [id, articlePage, articleRowsPerPage, productSearch, showSnackbar]);

  const onGetStockById = async () => {
    const response = await stockService.getStockById(id as string);
    setStockData(response as any);
  };

  useEffect(() => {
    if (id) {
      onGetStockById();
    }
  }, [id]);

  useEffect(() => {
    if (stockData) {
      reset({
        code: stockData.code || "",
        companyId: stockData.companyId || "",
        percentageSafety: stockData.percentageSafety || "",
        estimatedArribalDate: stockData.estimatedArribalDate
          ? new Date(stockData.estimatedArribalDate).toISOString().split("T")[0]
          : "",
        arribalDate: stockData.arribalDate
          ? new Date(stockData.arribalDate).toISOString().split("T")[0]
          : "",
        observation: stockData.observation || "",
      });
      setSocietyInputValue(stockData.company || "");
    }
  }, [stockData, reset]);

  const onCancelStock = async () => {
    try {
      setIsLoading(true);
      await stockService.cancelStock(id!, "");
      navigate("/stock");
      showSnackbar("Stock anulado correctamente", "success", 3000);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al anular stock",
        "error",
        4000,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/stock");
  };

  const handleClearSearch = () => {
    setProductSearch("");
    setArticlePage(0);
  };

  const handleArticlePageChange = (page: number) => {
    const newPage = page - 1; // DataTable usa páginas basadas en 1, API usa basadas en 0
    setArticlePage(newPage);
  };

  const handleOpenModal = () => {
    setEditingArticle(null);
    resetArticleForm({
      nombre: "",
      code: "",
      productId: "",
      cantidad: "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingArticle(null);
    resetArticleForm();
    setProductInputValue("");
  };

  const handleEditArticle = (article: ArticleItem) => {
    setEditingArticle(article);
    resetArticleForm({
      nombre: article.productDescription,
      productId: article.productId,
      code: article.productCode,
      cantidad: article.quantity.toString(),
    });
    setProductInputValue(article.productDescription);
    setOpenModal(true);
  };

  const handleDeleteArticle = async (stockItemId: string) => {
    try {
      // Aquí podrías llamar a un servicio para eliminar el item si existe un endpoint
      // Por ahora, solo actualizamos la lista local
      setArticles((prev) =>
        prev.filter((article) => article.stockItemId !== stockItemId),
      );
      showSnackbar("Artículo eliminado correctamente", "success", 3000);

      // Recargar los items
      if (id) {
        const response = await stockService.getItems(
          id,
          articlePage,
          articleRowsPerPage,
          productSearch,
        );
        setArticles(response?.stockItemResponses || []);
        setArticlesTotal(response?.total || 0);
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al eliminar el artículo",
        "error",
        4000,
      );
    }
  };

  const onArticleSubmit = async (data: ArticleFormData) => {
    if (!data.productId && !editingArticle) {
      showSnackbar("Debe seleccionar un producto de la lista", "error", 3000);
      return;
    }

    try {
      if (editingArticle) {
        const parsedQty = Number(data.cantidad);
        const nextQuantity = Number.isFinite(parsedQty)
          ? parsedQty
          : editingArticle.quantity;
        // Editar artículo existente en el array
        setArticles((prev) =>
          prev.map((article) =>
            article.stockItemId === editingArticle.stockItemId
              ? {
                  ...article,
                  productId: data.productId || editingArticle.productId,
                  code: data.code || editingArticle.code,
                  description: data.nombre || editingArticle.description,
                  quantity: nextQuantity,
                  rest: computeArticleRest(
                    nextQuantity,
                    Number(stockData?.percentageSafety ?? "0"),
                  ),
                  type: "EDIT",
                }
              : article,
          ),
        );
        // showSnackbar("Artículo actualizado correctamente", "success", 3000);
      } else {
        const alreadyExists = articles.some(
          (article) =>
            article.code.trim() === data.code.trim() &&
            article.stockItemId !== editingArticle?.stockItemId,
        );

        if (alreadyExists) {
          showSnackbar("El producto ya fue agregado al stock", "error", 3000);
          return;
        }

        // Agregar nuevo artículo al array
        const newArticle: any = {
          stockItemId: "00000000-0000-0000-0000-000000000000",
          productId: data.productId,
          code: data.code,
          description: data.nombre,
          quantity: Number(data.cantidad),
          rest: computeArticleRest(
            Number(data.cantidad),
            Number(stockData?.percentageSafety ?? "0"),
          ),
          type: "ADD",
        };
        setArticles((prev) => [...prev, newArticle]);
        // setArticlesTotal((prev) => prev + 1);
        // showSnackbar("Artículo agregado correctamente", "success", 3000);
      }

      handleCloseModal();
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al guardar el artículo",
        "error",
        4000,
      );
    }
  };

  const onSubmit = async (data: StockFormData) => {
    if (!id) return;

    try {
      setIsSaving(true);

      // Actualizar el encabezado del stock
      let response: any = await stockService.updateStock(id, {
        code: data?.code,
        percentageSafety: data?.percentageSafety,
        observation: data.observation,
        companyId: data.companyId,
        estimatedArribalDate: new Date(data.estimatedArribalDate).toISOString(),
      });

      // Filtrar los artículos que tienen type: "EDIT" o "ADD"
      const itemsToUpdate = articles.filter(
        (article) => article.type === "EDIT" || article.type === "ADD",
      );

      // Si hay items para actualizar, llamar al endpoint
      if (itemsToUpdate.length > 0) {
        const itemsPayload = itemsToUpdate.map((article) => ({
          stockHeaderId: id,
          stockItemId: article.stockItemId,
          productId: article.productId,
          quantity: article.quantity,
        }));

        await stockService.updateStockItems(itemsPayload);
      }

      showSnackbar("Stock actualizado correctamente", "success", 3000);
      navigate("/stock");
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al actualizar el stock",
        "error",
        4000,
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!stockData) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Form Card */}
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/home")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/stock")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Stocks
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Editar Stock
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Editar Stock
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete la información del stock
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              display: "flex",
              gap: 4,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* Form Fields */}
            <Box sx={{ flex: 1 }}>
              <Stack spacing={3}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="code"
                      control={control}
                      rules={{
                        required: "El código interno es requerido",
                      }}
                      render={({ field }) => (
                        <Input
                          label="Código Interno"
                          name="code"
                          field={field}
                          required
                          error={errors.code?.message}
                          icon={<QrCodeIcon />}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1, marginTop: 3.1 }}>
                    <TextField
                      label="Estado (no editable)"
                      value={stockData.status || ""}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="estimatedArribalDate"
                      control={control}
                      rules={{
                        required: "La fecha estimada de llegada es requerida",
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Fecha Estimada de Llegada"
                          type="date"
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                          error={!!errors.estimatedArribalDate}
                          helperText={errors.estimatedArribalDate?.message}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    {/* <Controller
                      name="arribalDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Fecha de Llegada"
                          type="date"
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                          error={!!errors.arribalDate}
                          helperText={errors.arribalDate?.message}
                        />
                      )}
                    /> */}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="companyId"
                      control={control}
                      rules={{
                        required: "La sociedad es requerida",
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          fullWidth
                          open={societyOpen}
                          onOpen={() => setSocietyOpen(true)}
                          onClose={() => setSocietyOpen(false)}
                          options={societies}
                          getOptionLabel={(option) => option.name}
                          loading={loadingSocieties}
                          inputValue={societyInputValue}
                          value={
                            societies.find(
                              (society) => society.companyId === value,
                            ) || null
                          }
                          onChange={(_, newValue) => {
                            onChange(newValue?.companyId || "");
                            // Actualizar el inputValue con el nombre de la sociedad seleccionada
                            setSocietyInputValue(newValue?.name || "");
                          }}
                          onInputChange={(_, newInputValue, reason) => {
                            // Actualizar el input value para búsqueda en tiempo real
                            if (reason === "input") {
                              setSocietyInputValue(newInputValue);
                            }
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.companyId === value?.companyId
                          }
                          noOptionsText="No se encontraron sociedades"
                          loadingText="Buscando sociedades..."
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Sociedad"
                              required
                              error={!!errors.companyId}
                              helperText={errors.companyId?.message}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingSocieties ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1, marginTop: "-1.2vw" }}>
                    <Controller
                      name="percentageSafety"
                      control={control}
                      rules={{
                        required: "El porcentaje de fallo es requerido",
                        pattern: {
                          value: /^[0-9]+$/,
                          message: "Debe ser un número entero válido",
                        },
                        validate: (value) => {
                          const num = parseInt(value);
                          if (num < 0 || num > 100) {
                            return "El porcentaje debe estar entre 0 y 100";
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          label="Porcentaje de fallo"
                          name="percentageSafety"
                          field={field}
                          required
                          type="number"
                          error={errors.percentageSafety?.message}
                          icon={<PercentIcon />}
                          sx={{ width: "100%" }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                <Box>
                  <Controller
                    name="observation"
                    control={control}
                    rules={{
                      required: "La observación es requerida",
                      minLength: {
                        value: 10,
                        message:
                          "La observación debe tener al menos 10 caracteres",
                      },
                      maxLength: {
                        value: 128,
                        message:
                          "La observación no puede exceder los 128 caracteres",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Observación"
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.observation}
                        helperText={errors.observation?.message}
                        placeholder="Ingrese observaciones sobre el stock..."
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Articles Section */}
          <Box sx={{ mt: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" component="h2">
                Listado de Productos
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenModal}
              >
                Agregar
              </Button>
            </Box>

            {/* Filtro de búsqueda */}
            <Box sx={{ mb: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: "center" }}
              >
                <TextField
                  label="Buscar producto"
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setArticlePage(0);
                  }}
                  placeholder="Buscar por código o nombre..."
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                  sx={{ minWidth: 120, height: 40 }}
                >
                  Limpiar
                </Button>
              </Stack>
            </Box>

            <DataTable
              columns={articleColumns}
              data={articles}
              page={articlePage + 1}
              isLoading={loadingArticles}
              onChange={handleArticlePageChange}
              total={articlesTotal}
            />
          </Box>

          {/* Article Modal */}
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {editingArticle ? "Editar Producto" : "Agregar Producto"}
            </DialogTitle>
            <DialogContent>
              <Box
                component="form"
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {editingArticle ? (
                  // Modo edición: mostrar campo bloqueado
                  // <Controller
                  //   name="nombre"
                  //   control={articleControl}
                  //   render={({ field }) => (
                  <TextField
                    // {...field}
                    value={editingArticle?.description || ""}
                    label="Nombre del Producto"
                    fullWidth
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                ) : (
                  // )}
                  // />
                  // Modo agregar: mostrar Autocomplete
                  <Controller
                    name="nombre"
                    control={articleControl}
                    rules={{
                      required: "El nombre del producto es requerido",
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        fullWidth
                        open={productOpen}
                        onOpen={() => setProductOpen(true)}
                        onClose={() => setProductOpen(false)}
                        options={products}
                        loading={loadingProducts}
                        inputValue={productInputValue}
                        value={
                          products.find(
                            (product) => product.description === value,
                          ) || null
                        }
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : `${option.code} - ${option.description}`
                        }
                        onChange={(_, newValue) => {
                          onChange(newValue?.description || "");
                          setProductInputValue(newValue?.description || "");
                          setArticleValue(
                            "productId",
                            newValue?.productID || "",
                            {
                              shouldValidate: true,
                            },
                          );
                          setArticleValue("code", newValue?.code || "", {
                            shouldValidate: true,
                          });
                        }}
                        onInputChange={(_, newInputValue, reason) => {
                          if (reason === "input") {
                            setProductInputValue(newInputValue);
                          }
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.productID === value.productID
                        }
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              fontFamily: "monospace",
                            }}
                          >
                            <Box
                              sx={{
                                width: CODE_WIDTH,
                                textAlign: "right",
                                pr: 1,
                                whiteSpace: "nowrap",
                                fontWeight: 500,
                              }}
                            >
                              {option.code}
                            </Box>
                            <Box sx={{ pr: 1 }}>-</Box>
                            <Box sx={{ flex: 1, whiteSpace: "nowrap" }}>
                              {option.description}
                            </Box>
                          </Box>
                        )}
                        noOptionsText="No se encontraron productos"
                        loadingText="Buscando productos..."
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Nombre del Producto"
                            required
                            error={
                              !!articleErrors.nombre ||
                              !!articleErrors.productId
                            }
                            helperText={
                              articleErrors.nombre?.message ||
                              articleErrors.productId?.message
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingProducts ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  />
                )}

                {/* Campo oculto para productId */}
                <Controller
                  name="code"
                  control={articleControl}
                  render={({ field }) => <input type="hidden" {...field} />}
                />

                {!editingArticle && (
                  <Controller
                    name="productId"
                    control={articleControl}
                    rules={{
                      required: "Debe seleccionar un producto de la lista",
                    }}
                    render={({ field }) => <input type="hidden" {...field} />}
                  />
                )}

                <Controller
                  name="cantidad"
                  control={articleControl}
                  rules={{
                    required: "La cantidad es requerida",
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "Debe ser un número válido",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      label="Cantidad"
                      name="cantidad"
                      field={field}
                      required
                      type="number"
                      error={articleErrors.cantidad?.message}
                      icon={<NumbersIcon />}
                      sx={{ width: "100%" }}
                    />
                  )}
                />

                <TextField
                  label="Resto (estimado localmente)"
                  value={restoPreview === null ? "" : String(restoPreview)}
                  fullWidth
                  disabled
                  helperText={`Cantidad − (${failurePct}% de fallo sobre la cantidad)`}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseModal} variant="outlined">
                Cancelar
              </Button>
              <Button
                onClick={handleArticleSubmit(onArticleSubmit)}
                variant="contained"
              >
                {editingArticle ? "Actualizar" : "Agregar"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ minWidth: 120 }}
              disabled={isLoading || isSaving}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              sx={{ minWidth: 120 }}
              disabled={isLoading || isSaving}
            >
              {isSaving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockEditPage;
